import React, { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Box, IconButton, TextField } from "@mui/material";
import apiClient from "../services/apiClient";
import { useDispatch, useSelector } from "react-redux";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MdDelete, MdFilterList, MdFilterListOff } from "react-icons/md";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import SearchIcon from "@mui/icons-material/Search";
import PageLoader from "../components/Loading/PageLoader";
import CustomButton from "../components/Button/CustomButton";
import CustomInputBox from "../components/Form/CustomInputBox";
import { setModal } from "../features/modal/ModalSlice";
import { formatDate } from "../utils/utils";
import toast from "react-hot-toast";
import { Phone } from "@mui/icons-material";

const VoiceMail = () => {
  const [voiceMail, setVoiceMail] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showFilter, setShowFilter] = useState(true);
  const observer = useRef();
  const userInfo = useSelector((state) => state.userInfo.user);
  const dispatch = useDispatch();
  const [filter, setFilter] = useState({
    user_from: "",
    user_to: "",
    start_date: "",
    end_date: "",
    domain: "",
    uuid: "",
    page: 1,
    limit: 10,
  });

  useEffect(() => {
    fetchVoiceMail();
  }, [filter]);

  const fetchVoiceMail = async (page = 1) => {
    try {
      setLoading(true);
      let payload = {
        ...filter,
        domain: userInfo?.company_domain,
        page,
      };

      if (userInfo?.role === "3") {
        if (!userInfo?.settings?.default_extension?.extension) {
          setHasMore(false);
          return;
        }

        payload = {
          ...payload,
          user_to: userInfo?.settings?.default_extension?.extension,
        };
      }

      const response = await apiClient.post(
        `${process.env.REACT_APP_VOIP_SERVER_BASE_URL}/voicemail_msgs`,
        payload
      );
      const newLogs = response?.data || [];

      setVoiceMail((prev) => [...prev, ...newLogs]);
      setHasMore(newLogs.length > 0 && newLogs.length === filter.limit);
    } catch (err) {
      console.log(err);
      setHasMore(false);
      toast.error("Network Error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVoiceMail = async (log) => {
    try {
      const payload = {
        uuid: [log],
      };

      await apiClient.delete(`${process.env.REACT_APP_VOIP_SERVER_BASE_URL}/calls/delete`, payload);
      setVoiceMail((prevVoiceMail) => prevVoiceMail.filter((calllog) => calllog?.uuid !== log));
      toast.success("deleted!");
    } catch {
      console.log("Error!");
      toast.error("Opps! Something went wrong");
    }
  };

  const lastLogRef = useRef(null);
  useEffect(() => {
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        const nextPage = Math.ceil(voiceMail.length / filter.limit) + 1;
        if (!hasMore) return;
        fetchVoiceMail(nextPage);
      }
    });

    if (lastLogRef.current) {
      observer.current.observe(lastLogRef.current);
    }
  }, [voiceMail, loading, hasMore]);

  const handleShowFilter = () => {
    setShowFilter(!showFilter);
  };

  const handleDialCall = (data = null) => {
    dispatch(setModal({ type: "CALL_DIALPAD", data }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter((prevData) => ({ ...prevData, [name]: value }));
    setVoiceMail([]);
  };

  const columns = useMemo(
    () => [
      { accessorKey: "user_to", header: "To" },
      { accessorKey: "user_from", header: "From" },
      {
        accessorKey: "message_len",
        header: "Durations",
      },
      {
        accessorKey: "created_epoch",
        header: "Date",
        Cell: ({ cell }) => {
          const data = cell.getValue();
          return formatDate(data + " GMT");
        },
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: voiceMail,
    enableColumnOrdering: true,
    enableRowSelection: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowActions: true,
    enablePagination: false,
    positionActionsColumn: "last",
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
        {userInfo?.role === "3" && (
          <IconButton disabled onClick={() => handleDialCall(row?.original?.user_from)}>
            <Phone />
          </IconButton>
        )}
        <IconButton
          disabled={!row?.original?.voicemail_url}
          onClick={() => window.open(`${row?.original?.voicemail_url}`, "_blank")}
        >
          <PlayArrowIcon />
        </IconButton>
        {userInfo?.role === "2" && (
          <IconButton disabled onClick={() => handleDeleteVoiceMail(row?.original?.uuid)}>
            <MdDelete />
          </IconButton>
        )}
      </Box>
    ),
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#F5F6F8",
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
      },
    },
  });

  return (
    <>
      {loading && <PageLoader text="Loading Voice Mail" />}

      <div className="pageTitle1 px-5 py-4">
        <h3>Voice Mail</h3>
        <p>{voiceMail?.length} Available</p>
      </div>

      <Box sx={{ padding: 2 }}>
        <div className="d-flex justify-content-between">
          <div className="w-50">
            <CustomInputBox
              icon={SearchIcon}
              value={filter?.user_from}
              name="user_from"
              onChange={handleFilterChange}
              placeholder="Search Voice Mails"
              type="text"
              backgroundColor={"#FFFFFF"}
              borderRadius="8px"
            />
          </div>

          <div className="d-flex justify-content-end gap-3 mb-4">
            <CustomButton
              text={(showFilter ? "Hide" : "Show") + " Filter"}
              iconPosition="left"
              icon={showFilter ? <MdFilterListOff /> : <MdFilterList />}
              onClick={handleShowFilter}
              sx={{ 
                backgroundColor: "#FFFFFF",
                color: "#000000",
                borderRadius: "8px",
                padding: "8px 16px",
                border: "1px solid #d9d9d9",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#f7f6ffff",
                  boxShadow: "none",
                }
              }}
            />
          </div>
        </div>

        {showFilter && (
          <div className="d-flex justify-content-start gap-3 mb-4">
            <input
              type="date"
              value={filter?.start_date}
              onChange={(e) => {
                setFilter((prev) => ({ ...prev, start_date: e.target.value || "" }));
                setVoiceMail([]);
              }}
              style={{
                width: "180px",
                padding: "8px 10px",
                borderRadius: "7px",
                border: "1px solid #ccc",
                fontSize: "16px",
              }}
            />
            <input
              type="date"
              value={filter?.end_date}
              onChange={(e) => {
                setFilter((prev) => ({ ...prev, end_date: e.target.value || "" }));
                setVoiceMail([]);
              }}
              style={{
                width: "180px",
                padding: "8px 10px",
                borderRadius: "7px",
                border: "1px solid #ccc",
                fontSize: "16px",
              }}
            />

            <CustomButton text={"Reset"} variant="text"
              sx={{ 
                backgroundColor: "#FFFFFF",
                color: "#000000",
                borderRadius: "8px",
                padding: "8px 16px",
                border: "1px solid #d9d9d9",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#f7f6ffff",
                  boxShadow: "none",
                }
              }}
            />
          </div>
        )}

        <MaterialReactTable table={table} />
        <div ref={lastLogRef} style={{ height: "1px" }} />
      </Box>
    </>
  );
};

export default VoiceMail;
